import Vue from "vue";
import * as Sentry from "@sentry/vue";
import { isNavigationFailure, NavigationFailureType } from "vue-router";
import { env } from "@/environment/environment";

export const DEFAULT_ERROR_MESSAGE = "We couldn't complete that action. Please try again.";
export const DATABASE_UNAVAILABLE_CODE = "database_temporarily_unavailable";
export const DATABASE_UNAVAILABLE_MESSAGE = "The database is temporarily unavailable. Please wait a few seconds and try again.";

export const errorState = Vue.observable({
  message: DEFAULT_ERROR_MESSAGE,
  color: "error",
  visible: false,
});

const isObject = (value: unknown): value is object =>
  (typeof value === "object" && value !== null) || typeof value === "function";

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  isObject(value) ? value as Record<string, unknown> : undefined;

const getString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

export const isDatabaseUnavailableError = (error: unknown): boolean => {
  const errorRecord = asRecord(error);
  const response = asRecord(errorRecord?.response);
  const source = response ?? errorRecord;
  const data = asRecord(source?.data);
  return data?.code === DATABASE_UNAVAILABLE_CODE;
};

const formatErrors = (errors: unknown): string | undefined => {
  if (typeof errors === "string") return getString(errors);
  if (!isObject(errors)) return undefined;

  const messages = Object.values(errors);
  return messages.length > 0 ? `Error(s): ${messages.join(" & ")}` : undefined;
};

export const getErrorMessage = (
  error: unknown,
  options: { dataIsErrors?: boolean } = {}
): string => {
  const errorRecord = asRecord(error);
  const response = asRecord(errorRecord?.response);
  const source = response ?? errorRecord;
  const data = asRecord(source?.data);
  const errors = options.dataIsErrors ? data : data?.errors;

  return formatErrors(errors)
    ?? formatErrors(data?.messages)
    ?? getString(data?.error)
    ?? getString(data?.errorMessage)
    ?? getString(errorRecord?.message)
    ?? DEFAULT_ERROR_MESSAGE;
};

export const showError = (message = DEFAULT_ERROR_MESSAGE) => {
  errorState.message = message;
  errorState.color = "error";
  errorState.visible = true;
};

export const showDatabaseUnavailable = () => {
  errorState.message = DATABASE_UNAVAILABLE_MESSAGE;
  const environment = String(env.VUE_APP_SENTRY_ENVIRONMENT).toLowerCase();
  errorState.color = environment === "prd" || environment === "production" ? "error" : "warning";
  errorState.visible = true;
};

export const handleError = (error: unknown) => {
  Sentry.captureException(error);
  if (isDatabaseUnavailableError(error)) {
    showDatabaseUnavailable();
  } else {
    showError();
  }
};

export const handleNavigationError = (error: unknown) => {
  if (
    isNavigationFailure(error, NavigationFailureType.redirected) ||
    isNavigationFailure(error, NavigationFailureType.duplicated) ||
    isNavigationFailure(error, NavigationFailureType.cancelled) ||
    isNavigationFailure(error, NavigationFailureType.aborted)
  ) {
    return;
  }

  handleError(error);
};

export const installGlobalErrorHandling = () => {
  const sentryErrorHandler = Vue.config.errorHandler;

  Vue.config.errorHandler = (error, vm, info) => {
    showError();

    if (sentryErrorHandler) {
      sentryErrorHandler(error, vm, info);
    } else {
      Sentry.captureException(error);
    }
  };
};
